import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getContractErrorMessage } from "@/lib/contractErrorMessages";
import { useState, useEffect } from "react";
import { calculateCertificationAmounts, validateCertificationRevisionPayload } from "../../../../../shared/contracts/certification-economics";
import { Pencil } from "lucide-react";
import { CertificationDocumentsPanel } from "./CertificationDocumentsPanel";

interface CertificationUICapabilities {
  canSubmit: boolean;
  canPmReview: boolean;
  canSendToClient: boolean;
  canClientAccept: boolean;
  canCancel: boolean;
  canEditDraft: boolean;
  canManageDocs?: boolean;
  canReadEconomics?: boolean;
  canManageAccounting?: boolean;
}

interface Props {
  certificationId: number;
  contractId: number;
  currency?: string;
  caps: CertificationUICapabilities;
  open: boolean;
  onClose: () => void;
  onMutated: () => void;
}

function money(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("es-ES", { style: "currency", currency });
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  pending_pm_review: "Pendiente revisión PM",
  pm_approved: "Aprobado PM",
  sent_to_client: "Enviado al cliente",
  client_accepted: "Aceptado por cliente",
  rejected: "Rechazado",
  cancelled: "Cancelado",
};

const MILESTONE_STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  ready_to_invoice: "Lista para facturar",
  accounting_notified: "Notificada a contabilidad",
  invoiced: "Facturada",
  sent_to_client: "Enviado al cliente",
  pending_collection: "Pendiente de cobro",
  collected: "Cobrado",
  cancelled: "Cancelado",
};

export function CertificationDetailDialog({ certificationId, contractId, currency = "EUR", caps, open, onClose, onMutated }: Props) {
  const utils = trpc.useUtils();
  const certQuery = trpc.contractCertificationGet.useQuery(
    { certificationId, contractId },
    { enabled: open && !!certificationId },
  );
  const milestoneQuery = trpc.contractCertificationLinkedMilestone.useQuery(
    { certificationId, contractId },
    { enabled: open && !!certificationId },
  );
  const readyMilestoneQuery = trpc.contractCertificationReadyMilestone.useQuery(
    { certificationId, contractId },
    { enabled: open && !!certificationId },
  );
  const timelineQuery = trpc.contractCertificationTimeline.useQuery(
    { certificationId, contractId },
    { enabled: open && !!certificationId },
  );
  const revisionsQuery = trpc.contractCertificationRevisions.useQuery({ certificationId, contractId }, { enabled: open && !!certificationId });
  const conformitiesQuery = trpc.contractCertificationExternalConformities.useQuery({ certificationId, contractId }, { enabled: open && caps.canReadEconomics === true });
  const openingBalancesQuery = trpc.contractCertificationOpeningBalances.useQuery({ contractId }, { enabled: open && caps.canReadEconomics === true });
  const accountingExpedientQuery = trpc.contractCertificationAccountingExpedient.useQuery(
    { certificationId, contractId },
    { enabled: open && !!certificationId && caps.canReadEconomics === true },
  );
  const revisionSaveMut = trpc.contractCertificationRevisionSave.useMutation();
  const generatePdfMut = trpc.contractCertificationRevisionGeneratePdf.useMutation();
  const recordSendMut = trpc.contractCertificationRevisionRecordSend.useMutation();
  const externalRecordMut = trpc.contractCertificationExternalConformityRecord.useMutation();
  const externalReconcileMut = trpc.contractCertificationExternalConformityReconcile.useMutation();
  const openingBalanceMut = trpc.contractCertificationOpeningBalanceRecord.useMutation();
  const dispatchExpedientMut = trpc.contractCertificationAccountingExpedientDispatch.useMutation();
  const [externalAmounts, setExternalAmounts] = useState<Record<number, string>>({});
  const [externalNatures, setExternalNatures] = useState<Record<number, string>>({});
  const [externalRefs, setExternalRefs] = useState<Record<number, string>>({});
  const [externalReasons, setExternalReasons] = useState<Record<number, string>>({});
  const [externalForecasts, setExternalForecasts] = useState<Record<number, string>>({});
  const [externalAcknowledge, setExternalAcknowledge] = useState<Record<number, boolean>>({});
  const [openingCutoff, setOpeningCutoff] = useState("");
  const [openingAmount, setOpeningAmount] = useState("");
  const [openingReconciliationNote, setOpeningReconciliationNote] = useState("");
  const [openingDescription, setOpeningDescription] = useState("");
  const [openingEvidenceRef, setOpeningEvidenceRef] = useState("");
  const [openingEvidenceId, setOpeningEvidenceId] = useState<number | null>(null);
  const [revisionReason, setRevisionReason] = useState("");
  const [editingRevisionId, setEditingRevisionId] = useState<number | null>(null);
  const [revisionForm, setRevisionForm] = useState<Record<string, string>>({});
  const [revisionBaseline, setRevisionBaseline] = useState("");
  const [showRevisionDiscardConfirm, setShowRevisionDiscardConfirm] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [sendNote, setSendNote] = useState("");
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState<string | null>(null);
  const transitionMut = trpc.contractCertificationTransition.useMutation();
  const declarePmMut = trpc.contractCertificationDeclarePmReviewed.useMutation();
  const [showDeclarePm, setShowDeclarePm] = useState(false);
  const updateMut = trpc.contractCertificationUpdate.useMutation();

  const [rejectionReason, setRejectionReason] = useState("");
  const [cancellationReason, setCancellationReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showSignedRevisionConfirm, setShowSignedRevisionConfirm] = useState(false);
  const [legacyTransitionTarget, setLegacyTransitionTarget] = useState<"sent_to_client" | "client_accepted" | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editPeriodStart, setEditPeriodStart] = useState("");
  const [editPeriodEnd, setEditPeriodEnd] = useState("");
  const [editGross, setEditGross] = useState("");
  const [editRetention, setEditRetention] = useState("");
  const [editDescription, setEditDescription] = useState("");
  // Tracks the latest committed updatedAt after a save/transition/document mutation,
  // so subsequent writes use the current server version rather than the stale closure value.
  const [activeUpdatedAt, setActiveUpdatedAt] = useState<string | null>(null);

  async function invalidateAccounting() {
    await Promise.all([
      utils.contractCertificationExternalConformities.invalidate({ certificationId, contractId }),
      utils.contractCertificationOpeningBalances.invalidate({ contractId }),
      utils.contractCertificationsEconomicContext.invalidate({ contractId }),
      utils.contractCertificationLinkedMilestone.invalidate({ certificationId, contractId }),
      utils.contractCertificationReadyMilestone.invalidate({ certificationId, contractId }),
      utils.contractCertificationAccountingExpedient.invalidate({ certificationId, contractId }),
    ]);
  }

  // Reset local version tracking whenever the dialog opens a different certification.
  useEffect(() => {
    setActiveUpdatedAt(null);
    setGeneratedPdfUrl(null);
    setShowDeclarePm(false);
  }, [certificationId, open]);

  const cert = certQuery.data;

  useEffect(() => {
    if (cert?.status !== "draft") setShowDeclarePm(false);
  }, [cert?.status]);

  const currentExpectedVersion = activeUpdatedAt ?? cert?.updatedAt ?? "";
  const revisionPayload = (revision: any) => revision?.payload ?? {};
  function openRevisionEditor(revision: any) {
    const p = revisionPayload(revision);
    const context = revision.contractSnapshot ?? {};
    const form = {
      periodStart: String(p.periodStart ?? cert?.periodStart ?? ""),
      periodEnd: String(p.periodEnd ?? cert?.periodEnd ?? ""),
      workPeriod: String(p.workPeriod ?? p.taxableBase ?? cert?.grossCertifiedAmount ?? ""),
      advances: String(p.advances ?? "0"),
      vatRatePercent: String(p.vatRatePercent ?? (Number(p.taxableBase) ? Number(p.vatAmount ?? 0) / Number(p.taxableBase) * 100 : 0)),
      withholdingRatePercent: String(p.withholdingRatePercent ?? (Number(p.taxableBase) ? Number(p.withholdingAmount ?? 0) / Number(p.taxableBase) * 100 : 0)),
      withholdingAmount: String(p.withholdingAmount ?? ""),
      previousCertified: String(p.previousCertified ?? "0"),
      awardAmount: String(p.awardAmount ?? context.originalContractAmount ?? "0"),
      certificationDate: String(p.certificationDate ?? cert?.periodEnd ?? ""),
      certificationType: String(p.certificationType ?? "Ordinaria"),
      adjustmentReference: String(p.adjustmentReference ?? ""),
      issuePlace: String(p.issuePlace ?? ""),
      contractorName: String(p.contractorName ?? ""),
      contractorTaxId: String(p.contractorTaxId ?? ""),
      contractorAddress: String(p.contractorAddress ?? ""),
      subcontractorName: String(p.subcontractorName ?? ""),
      subcontractorTaxId: String(p.subcontractorTaxId ?? ""),
      subcontractorAddress: String(p.subcontractorAddress ?? ""),
      description: String(p.description ?? cert?.description ?? ""),
    };
    setEditingRevisionId(Number(revision.id));
    setRevisionForm(form);
    setRevisionBaseline(JSON.stringify(form));
  }
  const revisionDirty = editingRevisionId != null && JSON.stringify(revisionForm) !== revisionBaseline;
  function requestRevisionEditorClose() {
    if (revisionDirty) setShowRevisionDiscardConfirm(true);
    else setEditingRevisionId(null);
  }
  async function saveRevisionEdit(revision: any) {
    try {
      const amounts = calculateCertificationAmounts({
        workPeriod: revisionForm.workPeriod, advances: revisionForm.advances || "0",
        vatRatePercent: revisionForm.vatRatePercent || "0",
        withholdingRatePercent: revisionForm.withholdingRatePercent || "0",
        withholdingAmount: revisionForm.withholdingAmount || undefined,
        previousCertified: revisionForm.previousCertified || "0",
        awardAmount: revisionForm.awardAmount || "0",
      });
      const payload = {
        ...revisionPayload(revision), ...amounts,
        withholdingRatePercent: revisionForm.withholdingRatePercent || "0",
        certificationDate: revisionForm.certificationDate || null,
        certificationType: revisionForm.certificationType || "Ordinaria",
        adjustmentReference: revisionForm.adjustmentReference || "",
        issuePlace: revisionForm.issuePlace || "",
        contractorName: revisionForm.contractorName || "",
        contractorTaxId: revisionForm.contractorTaxId || "",
        contractorAddress: revisionForm.contractorAddress || "",
        subcontractorName: revisionForm.subcontractorName || "",
        subcontractorTaxId: revisionForm.subcontractorTaxId || "",
        subcontractorAddress: revisionForm.subcontractorAddress || "",
        periodStart: revisionForm.periodStart, periodEnd: revisionForm.periodEnd,
        description: revisionForm.description.trim(),
      };
      await revisionSaveMut.mutateAsync({
        certificationId, contractId, revisionId: Number(revision.id),
        expectedUpdatedAt: revision.updatedAt, payload,
      });
      setEditingRevisionId(null);
      setGeneratedPdfUrl(null);
      await invalidate();
      onMutated();
      toast.success("Revisión actualizada. La revisión enviada permanece intacta.");
    } catch (e: any) {
      const msg = e?.message ?? "Error al guardar la revisión.";
      if (msg.includes("CERT_REVISION_STALE_UPDATE")) {
        toast.error("La revisión cambió en otra sesión. Recarga antes de volver a guardar.");
        await utils.contractCertificationRevisions.invalidate({ certificationId, contractId });
      } else {
        toast.error(getContractErrorMessage(msg));
      }
    }
  }

  function openEdit() {
    if (!cert) return;
    setEditPeriodStart(String(cert.periodStart ?? ""));
    setEditPeriodEnd(String(cert.periodEnd ?? ""));
    setEditGross(String(cert.grossCertifiedAmount ?? ""));
    setEditRetention(String(cert.retentionAmount ?? "0"));
    setEditDescription(String(cert.description ?? ""));
    setShowEdit(true);
  }

  async function invalidate() {
    await Promise.all([
      utils.contractCertificationsList.invalidate({ contractId }),
      utils.customers.contracts.economyMilestones.invalidate({ contractId }),
      utils.contractCertificationGet.invalidate({ certificationId, contractId }),
      utils.contractCertificationRevisions.invalidate({ certificationId, contractId }),
      utils.contractCertificationDocumentsList.invalidate({ certificationId, contractId }),
      utils.contractCertificationLinkedMilestone.invalidate({ certificationId, contractId }),
      utils.contractCertificationReadyMilestone.invalidate({ certificationId, contractId }),
      utils.contractCertificationAccountingExpedient.invalidate({ certificationId, contractId }),
      utils.contractCertificationsEconomicSummary.invalidate({ contractId }),
      utils.contractCertificationsEconomicContext.invalidate({ contractId }),
      utils.contractCertificationTimeline.invalidate({ certificationId, contractId }),
      ...(caps.canReadEconomics ? [utils.contractCertificationAccountingExpedient.invalidate({ certificationId, contractId }), utils.contractCertificationExternalConformities.invalidate({ certificationId, contractId }), utils.contractCertificationOpeningBalances.invalidate({ contractId })] : []),
    ]);
  }

  async function handleSaveEdit() {
    if (!cert) return;
    const gross = parseFloat(editGross.replace(",", "."));
    if (isNaN(gross) || gross <= 0) {
      toast.error("La base certificada sin IVA debe ser mayor que cero.");
      return;
    }
    const retention = parseFloat(editRetention.replace(",", ".")) || 0;
    if (retention < 0 || retention > gross) {
      toast.error("La retención contractual no puede ser negativa ni superar la base certificada sin IVA.");
      return;
    }
    if (!editPeriodStart || !editPeriodEnd) {
      toast.error("El período es obligatorio.");
      return;
    }
    try {
      const saveRes = await updateMut.mutateAsync({
        certificationId,
        contractId,
        periodStart: editPeriodStart,
        periodEnd: editPeriodEnd,
        grossCertifiedAmount: gross,
        retentionAmount: retention,
        description: editDescription.trim() || null,
        expectedUpdatedAt: currentExpectedVersion,
      });
      if (saveRes?.updatedAt) {
        setActiveUpdatedAt(saveRes.updatedAt);
      }
      toast.success("Certificación actualizada.");
      setShowEdit(false);
      await invalidate();
      onMutated();
    } catch (err: any) {
      const msg = err?.message ?? "Error al guardar.";
      if (msg.includes("CERT_STALE_UPDATE")) {
        toast.error("La certificación fue modificada por otro usuario. Recarga e inténtalo de nuevo.");
        utils.contractCertificationGet.invalidate({ certificationId, contractId });
      } else if (msg.includes("CERT_EDIT_NOT_DRAFT")) {
        toast.error("Solo se puede editar en estado borrador.");
      } else if (msg.includes("CERT_EXCEEDS_AVAILABLE_AMOUNT")) {
        toast.error("El importe supera el techo disponible del contrato.");
      } else {
        toast.error(getContractErrorMessage(msg));
      }
    }
  }

  async function declarePmReviewed() {
    if (!cert) return;
    try {
      const result = await declarePmMut.mutateAsync({ certificationId, contractId, expectedUpdatedAt: currentExpectedVersion });
      setActiveUpdatedAt(result.updatedAt);
      setShowDeclarePm(false);
      toast.success("Registrada como ya revisada por PM.");
      await invalidate();
      onMutated();
    } catch (err: any) {
      const msg = err?.message ?? "Error al registrar la revisión.";
      if (msg.includes("CERT_STALE_UPDATE")) {
        toast.error("La certificación fue modificada por otro usuario. Cierra y vuelve a abrir antes de confirmar.");
        setShowDeclarePm(false);
        setActiveUpdatedAt(null);
        await invalidate();
      } else {
        toast.error(getContractErrorMessage(msg));
      }
    }
  }

  async function doTransition(
    targetStatus: string,
    extra?: { rejectionReason?: string; cancellationReason?: string },
  ) {
    if (!cert) return;
    try {
      const transRes = await transitionMut.mutateAsync({
        certificationId,
        contractId,
        targetStatus: targetStatus as any,
        expectedUpdatedAt: currentExpectedVersion,
        ...(targetStatus === "client_accepted" && hasRevisionHistory ? { signedRevisionConfirmed: true } : {}),
        ...extra,
      });
      if (transRes?.updatedAt) {
        setActiveUpdatedAt(transRes.updatedAt);
      }
      toast.success("Estado actualizado.");
      await invalidate();
      onMutated();
      setShowReject(false);
      setShowCancel(false);
      setRejectionReason("");
      setCancellationReason("");
    } catch (err: any) {
      const msg = err?.message ?? "Error al cambiar estado.";
      if (msg.includes("CERT_STALE_UPDATE")) {
        toast.error("La certificación fue modificada por otro usuario. Recarga e inténtalo de nuevo.");
        utils.contractCertificationGet.invalidate({ certificationId, contractId });
      } else if (msg.includes("CERT_EXCEEDS_AVAILABLE_AMOUNT")) {
        toast.error("El importe supera el techo disponible.");
      } else {
        toast.error(getContractErrorMessage(msg));
      }
    }
  }

  const status = cert?.status ?? "";
  const revisionRows = Array.isArray(revisionsQuery.data) ? revisionsQuery.data : [];
  const revisionsLoaded = !revisionsQuery.isLoading && !revisionsQuery.isError && Array.isArray(revisionsQuery.data);
  const hasRevisionHistory = revisionsLoaded && revisionRows.length > 0;
  const legacyWithoutV1 = revisionsLoaded && revisionRows.length === 0;
  const revisionsUnavailable = revisionsQuery.isLoading || revisionsQuery.isError || !Array.isArray(revisionsQuery.data);
  const summaryRevision = revisionsLoaded ? revisionRows.find((r: any) => r.status === "preparing" || r.status === "sent" || r.status === "accepted") : null;
  let validatedSummaryAmounts: ReturnType<typeof validateCertificationRevisionPayload> | null = null;
  if (summaryRevision) {
    try { validatedSummaryAmounts = validateCertificationRevisionPayload(summaryRevision.payload); } catch { /* keep the draft payload for its editor; never project it as approved economics */ }
  }
  const summaryRequiresReview = hasRevisionHistory && (!summaryRevision || !validatedSummaryAmounts);

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setShowReject(false); setShowCancel(false); setShowDeclarePm(false); onClose(); } }}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {cert ? `Certificación #${cert.certificationNumber}` : "Certificación"}
          </DialogTitle>
          <DialogDescription className="sr-only">Detalle, acciones e historial de la certificación contractual.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 min-w-0 [overflow-wrap:anywhere]">
        {certQuery.isLoading ? (
          <div className="space-y-3"><Skeleton className="h-40 w-full" /></div>
        ) : certQuery.isError ? (
          <p role="alert" className="text-sm text-destructive">No se pudo cargar esta certificación para el contrato indicado. Cierra el detalle e inténtalo de nuevo.</p>
        ) : !cert ? (
          <p role="alert" className="text-sm text-muted-foreground">No se encontró esta certificación para el contrato indicado.</p>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant={
                status === "client_accepted" ? "default"
                : status === "rejected" || status === "cancelled" ? "destructive"
                : status === "draft" ? "secondary" : "outline"
              }>
                {STATUS_LABELS[status] ?? "Estado registrado"}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-muted-foreground">Período</span>
                <p>{cert.periodStart} — {cert.periodEnd}</p>
              </div>
              {(() => {
                if (!revisionsLoaded) return <div className="col-span-2 border-t border-border/60 pt-2 text-xs text-muted-foreground" role={revisionsQuery.isError ? "alert" : undefined}>
                  {revisionsQuery.isError ? "No se pudieron cargar las revisiones; el resumen y las acciones históricas no están disponibles." : "Cargando revisiones; el resumen histórico no está disponible todavía."}
                </div>;
                if (summaryRequiresReview) return <div className="col-span-2 border-t border-border/60 pt-2 text-xs text-amber-800 dark:text-amber-300" role="alert">
                  Revisión económica requerida: el snapshot V1 está incompleto o no se puede verificar. No se muestran importes como aprobados. Los datos capturados siguen disponibles en el editor de la revisión.
                </div>;
                if (summaryRevision && validatedSummaryAmounts) return <div className="col-span-2 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-border/60 pt-2 text-xs" aria-label="Resumen económico de revisión">
                  <span className="text-muted-foreground">Base imponible</span><span className="text-right">{money(validatedSummaryAmounts.taxableBase, currency)}</span>
                  <span className="text-muted-foreground">IVA</span><span className="text-right">{money(validatedSummaryAmounts.vatAmount, currency)}</span>
                  <span className="text-muted-foreground">Retención</span><span className="text-right">{money(validatedSummaryAmounts.withholdingAmount, currency)}</span>
                  <span className="font-medium">Líquido</span><span className="text-right font-medium">{money(validatedSummaryAmounts.payable, currency)}</span>
                  <span className="text-muted-foreground">Número de revisión</span><span className="text-right">{summaryRevision.revisionNumber}</span>
                </div>;
                return <>
                  <div><span className="text-muted-foreground">Base certificada sin IVA</span><p className="font-medium">{money(cert.grossCertifiedAmount, currency)}</p></div>
                  <div><span className="text-muted-foreground">Retención contractual</span><p>{money(cert.retentionAmount, currency)}</p></div>
                  <div><span className="text-muted-foreground">Líquido tras retención</span><p className="font-medium">{money(cert.netCertifiedAmount, currency)}</p></div>
                </>;
              })()}
              {revisionsLoaded && !summaryRequiresReview && cert.cumulativeCertifiedAmount != null && (
                <div className="col-span-2 min-w-0">
                  <span className="text-muted-foreground">Acumulado aceptado</span>
                  <p>{money(cert.cumulativeCertifiedAmount, currency)}</p>
                </div>
              )}
              {cert.description && (
                <div className="col-span-2 min-w-0">
                  <span className="text-muted-foreground">Descripción</span>
                  <p>{cert.description}</p>
                </div>
              )}
              {cert.rejectionReason && (
                <div className="col-span-2 min-w-0 text-destructive">
                  <span className="font-medium">Motivo de rechazo:</span> {cert.rejectionReason}
                </div>
              )}
              {cert.cancellationReason && (
                <div className="col-span-2 min-w-0 text-destructive">
                  <span className="font-medium">Motivo de cancelación:</span> {cert.cancellationReason}
                </div>
              )}
            </div>

            {caps.canReadEconomics && (
              <section className="space-y-3 rounded border p-3" aria-labelledby="cert-accounting-workbench">
                <h3 id="cert-accounting-workbench" className="font-medium text-sm">Contabilidad · conformidad y saldo de apertura</h3>
                {caps.canManageAccounting && revisionsQuery.data?.some((r: any) => r.status === "accepted") && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Registra el importe observado en el portal/fuente externa para una revisión aceptada. Las diferencias bloquean facturación hasta conciliarlas.</p>
                    {(revisionsQuery.data as any[]).filter((r) => r.status === "accepted").map((revision: any) => (
                      <div key={`ext-${revision.id}`} className="grid grid-cols-1 gap-2 rounded border p-2 sm:grid-cols-2">
                        <Label className="text-xs">Revisión {revision.revisionNumber}
                          <Input aria-label={`Importe externo revisión ${revision.revisionNumber}`} inputMode="decimal" value={externalAmounts[revision.id] ?? ""} onChange={(e) => setExternalAmounts({ ...externalAmounts, [revision.id]: e.target.value })} placeholder="Importe" />
                        </Label>
                        <Label className="text-xs">Naturaleza del importe
                          <select className="h-10 w-full rounded border bg-background px-2" aria-label={`Naturaleza revisión ${revision.revisionNumber}`} value={externalNatures[revision.id] ?? "payable"} onChange={(e) => setExternalNatures({ ...externalNatures, [revision.id]: e.target.value })}>
                            <option value="taxable_base">Base imponible</option><option value="vat">IVA</option><option value="gross">Total antes de retención</option><option value="withholding">Retención</option><option value="payable">Líquido</option>
                          </select>
                        </Label>
                        <Label className="text-xs">Referencia del portal
                          <Input value={externalRefs[revision.id] ?? ""} onChange={(e) => setExternalRefs({ ...externalRefs, [revision.id]: e.target.value })} />
                        </Label>
                        <Label className="text-xs">Moneda
                          <Input value={currency} disabled readOnly />
                        </Label>
                        <div className="sm:col-span-2"><CertificationDocumentsPanel certificationId={certificationId} contractId={contractId} canManageDocs={caps.canManageDocs ?? false} revisionId={Number(revision.id)} initialDocumentType="external_evidence" /></div>
                        <Button size="sm" disabled={externalRecordMut.isPending || !(revision as any).id} onClick={async () => {
                          try {
                            const docs = await utils.contractCertificationDocumentsList.fetch({ certificationId, contractId });
                            const evidence = docs.find((d: any) => d.revisionId === Number(revision.id) && d.documentType === "external_evidence" && !d.archivedAt);
                            if (!evidence) throw new Error("Sube primero una evidencia externa en Documentos de esta revisión.");
                            const nature = (externalNatures[revision.id] ?? "payable") as "taxable_base"|"vat"|"gross"|"withholding"|"payable";
                            await externalRecordMut.mutateAsync({ certificationId, contractId, revisionId: Number(revision.id), amount: externalAmounts[revision.id] ?? "", currency, amountNature: nature, reference: externalRefs[revision.id], evidenceDocumentId: Number(evidence.id), confirmedAt: new Date() });
                            toast.success("Conformidad externa registrada; queda pendiente de conciliación."); await invalidateAccounting();
                          } catch (e: any) { toast.error(e?.message ?? "No se pudo registrar la conformidad."); }
                        }}>Registrar importe externo</Button>
                      </div>
                    ))}
                  </div>
                )}
                {(conformitiesQuery.data as any[] | undefined)?.map((item: any) => (
                  <div key={item.id} className="space-y-2 rounded border p-2 text-xs">
                    <p>Rev. {item.revisionNumber} · {({taxable_base:"base imponible",vat:"IVA",gross:"total antes de retención",withholding:"retención",payable:"líquido"} as Record<string,string>)[item.amountNature] ?? item.amountNature} · referencia {item.reference || "—"} · moneda {item.currency} · recibido {money(item.amount, item.currency)} · esperado {money(item.expectedAmount, item.currency)} · diferencia {money(item.differenceAmount, item.currency)} · {item.status === "reconciled" ? "Conciliado" : "Pendiente de conciliación"}</p>
                    {item.evidenceDocumentId && <a className="underline" href={`/api/contract-certification-documents/${item.evidenceDocumentId}/download?contractId=${contractId}`} target="_blank" rel="noreferrer">Abrir evidencia externa</a>}
                    {caps.canManageAccounting && item.status === "pending" && (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Label>Motivo<Input value={externalReasons[item.id] ?? ""} onChange={(e) => setExternalReasons({ ...externalReasons, [item.id]: e.target.value })} /></Label>
                        <Label>Fecha prevista de factura<Input type="date" value={externalForecasts[item.id] ?? ""} onChange={(e) => setExternalForecasts({ ...externalForecasts, [item.id]: e.target.value })} /></Label>
                        {Number(item.differenceAmount) !== 0 && <label className="flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={externalAcknowledge[item.id] ?? false} onChange={(e) => setExternalAcknowledge({ ...externalAcknowledge, [item.id]: e.target.checked })} />Reconozco y justifico la diferencia para habilitar factura</label>}
                        <Button size="sm" disabled={externalReconcileMut.isPending} onClick={async () => {
                          try {
                            const docs = await utils.contractCertificationDocumentsList.fetch({ certificationId, contractId });
                            const evidence = docs.find((d: any) => d.revisionId === Number(item.revisionId) && ["external_evidence","acceptance_evidence"].includes(d.documentType) && !d.archivedAt);
                            if (!evidence) throw new Error("Falta evidencia ligada a esta revisión.");
                            await externalReconcileMut.mutateAsync({ conformityId: Number(item.id), contractId, reason: externalReasons[item.id] ?? "", evidenceDocumentId: Number(evidence.id), invoiceForecastDate: externalForecasts[item.id] ?? "", acknowledgeDifference: externalAcknowledge[item.id] ?? false });
                            toast.success("Conformidad conciliada."); await invalidateAccounting();
                          } catch (e: any) { toast.error(e?.message ?? "No se pudo conciliar."); }
                        }}>Conciliar y actualizar bloqueo</Button>
                      </div>
                    )}
                  </div>
                ))}
                {caps.canManageAccounting && (
                  <div className="space-y-2 rounded border border-dashed p-3">
                    <p className="text-sm font-medium">Saldo inicial validado</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Label>Corte de saldo<Input type="date" value={openingCutoff} onChange={(e) => setOpeningCutoff(e.target.value)} /></Label>
                      <Label>Importe base acumulado a origen<Input inputMode="decimal" value={openingAmount} onChange={(e) => setOpeningAmount(e.target.value)} /></Label>
                      <Label>Origen/alcance<Input value={openingDescription} onChange={(e) => setOpeningDescription(e.target.value)} /></Label>
                      <Label>Referencia evidencia<Input value={openingEvidenceRef} onChange={(e) => setOpeningEvidenceRef(e.target.value)} /></Label>
                      <Label className="text-xs sm:col-span-2">Nota de conciliación histórica (si el importe difiere del histórico conocido)
                        <Input value={openingReconciliationNote} onChange={(e) => setOpeningReconciliationNote(e.target.value)} placeholder="Explica el ajuste conciliado contra el mayor" />
                      </Label>
                      <p className="text-xs text-muted-foreground sm:col-span-2">El saldo es el acumulado bruto histórico a la fecha de corte e incluye certificaciones previas conocidas. Las fechas de corte se registran en orden ascendente.</p>
                    </div>
                    <CertificationDocumentsPanel certificationId={certificationId} contractId={contractId} canManageDocs={caps.canManageDocs ?? false} initialDocumentType="otro" />
                    <Label className="text-xs">Evidencia exacta a vincular
                      <select className="h-10 w-full rounded border bg-background px-2" value={openingEvidenceId ?? ""} onChange={(e) => setOpeningEvidenceId(e.target.value ? Number(e.target.value) : null)}>
                        <option value="">Selecciona un documento del expediente</option>
                        {(utils.contractCertificationDocumentsList.getData({ certificationId, contractId }) as any[] | undefined)?.filter((d: any) => d.documentType === "otro" && !d.archivedAt).map((d: any) => <option key={d.id} value={d.id}>{d.originalFilename} · #{d.id}</option>)}
                      </select>
                    </Label>
                    <Button size="sm" disabled={openingBalanceMut.isPending} onClick={async () => {
                      try {
                        const docs = await utils.contractCertificationDocumentsList.fetch({ certificationId, contractId });
                        const evidence = openingEvidenceId ? docs.find((d: any) => d.id === openingEvidenceId && d.documentType === "otro" && !d.archivedAt) : undefined;
                        if (!evidence) throw new Error("Adjunta un documento de evidencia al expediente antes de validar el saldo.");
                        await openingBalanceMut.mutateAsync({ contractId, certificationId, evidenceDocumentId: Number(evidence.id), cutoffDate: openingCutoff, amount: openingAmount, currency, sourceDescription: openingDescription, evidenceReference: openingEvidenceRef, reconciliationNote: openingReconciliationNote || undefined });
                        toast.success("Saldo inicial validado y registrado."); setOpeningCutoff("");setOpeningAmount("");setOpeningReconciliationNote("");setOpeningDescription("");setOpeningEvidenceRef(""); await invalidateAccounting();
                      } catch (e: any) { toast.error(e?.message ?? "No se pudo validar el saldo inicial."); }
                    }}>Validar saldo inicial</Button>
                    {(openingBalancesQuery.data as any[] | undefined)?.map((balance: any) => <p key={balance.id} className="text-xs">Corte {String(balance.cutoffDate).slice(0,10)} · {money(balance.amount,balance.currency)} · histórico conocido {money(balance.knownCumulativeAmount,balance.currency)} · ajuste {money(balance.reconciliationDifference,balance.currency)} · {balance.sourceDescription} · {balance.originalFilename ?? balance.evidenceReference}{balance.evidenceDocumentId && <> · <a className="underline" href={`/api/contract-certification-documents/${balance.evidenceDocumentId}/download?contractId=${contractId}`} target="_blank" rel="noreferrer">evidencia</a></>}</p>)}
                  </div>
                )}
              </section>
            )}

            {caps.canReadEconomics && revisionsQuery.data?.some((r: any) => r.status === "accepted") ? (
              <section aria-labelledby="cert-accounting-expedient" className="rounded border p-3 bg-muted/40 space-y-2">
                <h3 id="cert-accounting-expedient" className="font-medium text-sm">Expediente recibido por contabilidad</h3>
                {revisionsQuery.data?.filter((r: any) => r.status === "accepted").map((revision: any) => (
                  <div key={`dispatch-${revision.id}`} className="rounded border p-2">
                    {caps.canManageAccounting && <Button size="sm" disabled={dispatchExpedientMut.isPending} onClick={async () => {
                      try { const result = await dispatchExpedientMut.mutateAsync({ certificationId, contractId, revisionId: Number(revision.id) }); toast.success(`Expediente registrado como tarea ${result.taskStatus ?? "received"}; estado de correo: ${result.emailDeliveryStatus === "dry_run" ? "simulación, no enviado" : result.emailDeliveryStatus ?? "no solicitado"}.`); await invalidateAccounting(); }
                      catch (e: any) { toast.error(e?.message ?? "No se pudo entregar el expediente."); }
                    }}>Entregar rev. {revision.revisionNumber} a Contabilidad</Button>}
                  </div>
                ))}
                {accountingExpedientQuery.data.map((event: any) => (
                  <div key={event.id} className="space-y-1 text-xs">
                    <p>Revisión {event.revisionNumber} · {new Date(event.createdAt).toLocaleString("es-ES")} · tarea {event.taskStatus ?? "received"} · correo {({not_requested:"no solicitado",queued:"en cola",sent:"enviado",failed:"falló · reintentable",dry_run:"simulado · no enviado",disabled:"deshabilitado"} as Record<string,string>)[event.emailDeliveryStatus] ?? event.emailDeliveryStatus ?? "no solicitado"}</p>
                    <p className="break-all text-muted-foreground">Snapshot SHA-256 · {event.snapshotHash}</p>
                    <p>Siguiente paso · {(event.payloadSnapshot?.task?.nextAction === "reconcile_external_conformity" ? "conciliar conformidad externa" : event.payloadSnapshot?.task?.nextAction === "prepare_invoice" ? "preparar factura" : "registrar previsión de factura")}</p>
                    {event.payloadSnapshot?.contract?.customer?.legalName && <p>Cliente · {event.payloadSnapshot.contract.customer.legalName} · facturar a {event.payloadSnapshot.contract.billTo?.legalName ?? "—"}</p>}
                    {event.payloadSnapshot?.contract?.projectManager?.name && <p>PM · {event.payloadSnapshot.contract.projectManager.name}</p>}
                    {event.payloadSnapshot?.certification?.economics && <p>Base {money(event.payloadSnapshot.certification.economics.taxableBase,currency)} · IVA {money(event.payloadSnapshot.certification.economics.vatAmount,currency)} · retención {money(event.payloadSnapshot.certification.economics.withholdingAmount,currency)} · líquido {money(event.payloadSnapshot.certification.economics.payable,currency)}</p>}
                    {(event.payloadSnapshot?.documents ?? []).map((doc: any) => <p key={doc.id}><a className="underline" href={doc.downloadPath} target="_blank" rel="noreferrer">{doc.filename ?? `Documento #${doc.id}`}</a> · {doc.documentType}</p>)}
                    {event.signedDocumentId && <a className="underline" href={`/api/contract-certification-documents/${event.signedDocumentId}/download?contractId=${contractId}`} target="_blank" rel="noreferrer">Abrir firmado recibido</a>}
                  </div>
                ))}
              </section>
            ) : null}

            {milestoneQuery.data && (
              <div className="rounded border p-3 bg-muted/40 space-y-1">
                <p className="font-medium text-xs text-muted-foreground">Hito de facturación vinculado</p>
                <p className="text-sm">{milestoneQuery.data.label} — <span className="font-medium">{money(milestoneQuery.data.fixedAmount, currency)}</span></p>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  <span>Estado: <span className="text-foreground">{MILESTONE_STATUS_LABELS[milestoneQuery.data.status] ?? "Estado registrado"}</span></span>
                  {readyMilestoneQuery.data?.id === milestoneQuery.data.id && (
                    <Badge variant="default" className="text-[10px]">Listo para facturar</Badge>
                  )}
                  {milestoneQuery.data.dueDate && <span>Vencimiento: {milestoneQuery.data.dueDate}</span>}
                  {milestoneQuery.data.calculatedAmount && (
                    <span>Calculado: {money(milestoneQuery.data.calculatedAmount, currency)}</span>
                  )}
                </div>
              </div>
            )}

            {/* Draft edit form */}
            {status === "draft" && caps.canEditDraft && showEdit && (
              <div className="space-y-3 rounded border p-3">
                <p className="text-xs font-medium">Editar borrador</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="edit-period-start" className="text-xs">Inicio período</Label>
                    <Input id="edit-period-start" type="date" value={editPeriodStart} onChange={(e) => setEditPeriodStart(e.target.value)} className="h-8 text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="edit-period-end" className="text-xs">Fin período</Label>
                    <Input id="edit-period-end" type="date" value={editPeriodEnd} onChange={(e) => setEditPeriodEnd(e.target.value)} className="h-8 text-xs" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-gross" className="text-xs">Base certificada sin IVA (€)</Label>
                  <Input id="edit-gross" type="number" min="0" step="0.01" value={editGross} onChange={(e) => setEditGross(e.target.value)} className="h-8 text-xs" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-retention" className="text-xs">Retención contractual (€)</Label>
                  <Input id="edit-retention" type="number" min="0" step="0.01" value={editRetention} onChange={(e) => setEditRetention(e.target.value)} className="h-8 text-xs" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-desc" className="text-xs">Descripción</Label>
                  <Textarea id="edit-desc" rows={2} value={editDescription} onChange={(e) => setEditDescription(e.target.value)} className="text-xs" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveEdit} disabled={updateMut.isPending}>
                    {updateMut.isPending ? "Guardando…" : "Guardar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
                </div>
              </div>
            )}

            <Separator />
            <section aria-labelledby="cert-revision-title" className="space-y-3">
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2"><h3 id="cert-revision-title" className="font-medium">Revisiones y documentos</h3>{revisionsLoaded && <span className="text-xs text-muted-foreground">{revisionRows.length} revisiones</span>}</div>
              {revisionsQuery.isLoading && <p className="text-xs text-muted-foreground" role="status">Cargando revisiones…</p>}
              {revisionsQuery.isError && <p className="text-xs text-destructive" role="alert">No se pudieron cargar las revisiones. No se habilita el flujo histórico mientras no se verifique si existe una carátula V1.</p>}
              {legacyWithoutV1 && <div className="space-y-1 rounded-md bg-muted/30 p-3 text-xs" role="note" aria-label="Registro histórico sin revisión V1"><p className="font-medium">Registro histórico · sin carátula V1</p><p>No hay revisiones V1 asociadas. El snapshot económico y la carátula no están disponibles; se conserva el registro legacy y sus documentos existentes. Este flujo no crea una revisión ni modifica documentos históricos.</p></div>}
              {revisionsLoaded && revisionRows.map((revision: any) => (
                <div key={revision.id} className="space-y-2 rounded-md bg-muted/30 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-medium">Revisión {revision.revisionNumber}</p><Badge variant={revision.status === "accepted" ? "default" : "outline"}>{revision.status === "preparing" ? "En preparación" : revision.status === "sent" ? "Enviada" : revision.status === "accepted" ? "Aceptada" : revision.status === "superseded" ? "Sustituida" : revision.status}</Badge></div>
                  {revision.changeReason && <p className="text-xs text-muted-foreground">Cambio: {revision.changeReason}</p>}
                  {revision.status === "preparing" && ["draft", "sent_to_client"].includes(status) && caps.canEditDraft && (
                    <div className="space-y-3">
                      {editingRevisionId !== Number(revision.id) ? <Button size="sm" variant="outline" onClick={() => openRevisionEditor(revision)}><Pencil className="mr-1 h-3 w-3"/>Editar revisión en preparación</Button> : (
                        <div className="space-y-3 rounded-md border border-border/60 p-3">
                          <p className="text-xs text-muted-foreground">Esta carátula pertenece únicamente a la revisión {revision.revisionNumber}; los datos de identidad del contrato y la revisión enviada no se modificarán.</p>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div className="space-y-1"><Label>Inicio del período</Label><Input aria-label="Inicio del período de revisión" type="date" value={revisionForm.periodStart ?? ""} onChange={e => setRevisionForm(v => ({...v, periodStart:e.target.value}))}/></div>
                            <div className="space-y-1"><Label>Fin del período</Label><Input aria-label="Fin del período de revisión" type="date" value={revisionForm.periodEnd ?? ""} onChange={e => setRevisionForm(v => ({...v, periodEnd:e.target.value}))}/></div>
                          </div>
                          <div className="space-y-1"><Label>Obra ejecutada en el período (€)</Label><Input aria-label="Obra ejecutada en el período" inputMode="decimal" value={revisionForm.workPeriod ?? ""} onChange={e => setRevisionForm(v => ({...v, workPeriod:e.target.value}))}/></div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1"><Label>Anticipos / abonos (€)</Label><Input aria-label="Anticipos y abonos" inputMode="decimal" value={revisionForm.advances ?? ""} onChange={e => setRevisionForm(v => ({...v, advances:e.target.value}))}/></div>
                            <div className="space-y-1"><Label>IVA (%)</Label><Input aria-label="IVA de revisión" inputMode="decimal" value={revisionForm.vatRatePercent ?? ""} onChange={e => setRevisionForm(v => ({...v, vatRatePercent:e.target.value}))}/></div>
                            <div className="space-y-1"><Label>Retención (%)</Label><Input aria-label="Retención porcentual" inputMode="decimal" value={revisionForm.withholdingRatePercent ?? ""} onChange={e => setRevisionForm(v => ({...v, withholdingRatePercent:e.target.value}))}/></div>
                            <div className="space-y-1"><Label>Retención alternativa (€)</Label><Input aria-label="Retención alternativa" inputMode="decimal" value={revisionForm.withholdingAmount ?? ""} onChange={e => setRevisionForm(v => ({...v, withholdingAmount:e.target.value}))}/></div>
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div className="space-y-1"><Label>Certificado anterior a origen (€)</Label><Input aria-label="Certificado anterior a origen" inputMode="decimal" value={revisionForm.previousCertified ?? ""} onChange={e => setRevisionForm(v => ({...v, previousCertified:e.target.value}))}/></div>
                            <div className="space-y-1"><Label>Fecha de certificación</Label><Input aria-label="Fecha de certificación" type="date" value={revisionForm.certificationDate ?? ""} onChange={e => setRevisionForm(v => ({...v, certificationDate:e.target.value}))}/></div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1"><Label>Tipo</Label><select aria-label="Tipo de certificación" className="h-9 w-full rounded-md border bg-background px-3" value={revisionForm.certificationType ?? "Ordinaria"} onChange={e => setRevisionForm(v => ({...v, certificationType:e.target.value}))}><option>Ordinaria</option><option>Ampliación</option><option>Liquidación</option></select></div>
                            <div className="space-y-1"><Label>Referencia de modificación</Label><Input aria-label="Referencia de modificación" value={revisionForm.adjustmentReference ?? ""} onChange={e => setRevisionForm(v => ({...v, adjustmentReference:e.target.value}))}/></div>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-2">
                            <div className="space-y-2"><p className="text-xs font-medium">Contratista</p><Input aria-label="Razón social contratista revisión" value={revisionForm.contractorName ?? ""} onChange={e => setRevisionForm(v => ({...v, contractorName:e.target.value}))}/><Input aria-label="NIF contratista revisión" value={revisionForm.contractorTaxId ?? ""} onChange={e => setRevisionForm(v => ({...v, contractorTaxId:e.target.value}))}/><Input aria-label="Domicilio contratista revisión" value={revisionForm.contractorAddress ?? ""} onChange={e => setRevisionForm(v => ({...v, contractorAddress:e.target.value}))}/></div>
                            <div className="space-y-2"><p className="text-xs font-medium">Subcontratista</p><Input aria-label="Razón social subcontratista revisión" value={revisionForm.subcontractorName ?? ""} onChange={e => setRevisionForm(v => ({...v, subcontractorName:e.target.value}))}/><Input aria-label="NIF subcontratista revisión" value={revisionForm.subcontractorTaxId ?? ""} onChange={e => setRevisionForm(v => ({...v, subcontractorTaxId:e.target.value}))}/><Input aria-label="Domicilio subcontratista revisión" value={revisionForm.subcontractorAddress ?? ""} onChange={e => setRevisionForm(v => ({...v, subcontractorAddress:e.target.value}))}/></div>
                          </div>
                          <div className="space-y-1"><Label>Lugar de emisión</Label><Input aria-label="Lugar de emisión de revisión" value={revisionForm.issuePlace ?? ""} onChange={e => setRevisionForm(v => ({...v, issuePlace:e.target.value}))}/></div>
                          <div className="space-y-1"><Label>Descripción</Label><Textarea aria-label="Descripción de revisión" rows={2} value={revisionForm.description ?? ""} onChange={e => setRevisionForm(v => ({...v, description:e.target.value}))}/></div>
                          <div className="flex flex-wrap gap-2"><Button size="sm" onClick={() => saveRevisionEdit(revision)} disabled={revisionSaveMut.isPending}>{revisionSaveMut.isPending ? "Guardando…" : "Guardar cambios de revisión"}</Button><Button size="sm" variant="outline" onClick={requestRevisionEditorClose}>Cancelar</Button></div>
                        </div>
                      )}
                    </div>
                  )}
                  {revision.contentHash && <p className="break-all text-[10px] text-muted-foreground">SHA-256 · {revision.contentHash}</p>}
                  {revision.downloadUrl && <a className="text-sm underline" href={revision.downloadUrl} target="_blank" rel="noreferrer">Abrir PDF de revisión</a>}
                  {revision.status === "preparing" && ["draft", "sent_to_client"].includes(status) && caps.canEditDraft && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" disabled={generatePdfMut.isPending} onClick={async () => { try { const r = await generatePdfMut.mutateAsync({ certificationId, contractId, revisionId: revision.id }); setGeneratedPdfUrl(r.downloadUrl); await invalidate(); toast.success("PDF generado y almacenado de forma privada."); } catch (e: any) { toast.error(getContractErrorMessage(e)); } }}>{generatePdfMut.isPending ? "Generando…" : "Generar / abrir PDF"}</Button>
                      {generatedPdfUrl && <a className="self-center text-sm underline" href={generatedPdfUrl} target="_blank" rel="noreferrer">Vista previa</a>}
                    </div>
                  )}
              {revision.status === "preparing" && ["draft", "sent_to_client"].includes(status) && caps.canSendToClient && editingRevisionId !== Number(revision.id) && (
                    <div className="space-y-2"><div className="grid gap-2 sm:grid-cols-[1fr_auto]"><Input aria-label="Correo destinatario" type="email" value={sentTo} onChange={e => setSentTo(e.target.value)} placeholder="destinatario@cliente.es"/><Button size="sm" disabled={!sentTo.includes("@") || recordSendMut.isPending || !revision.generatedPdfDocumentId} onClick={async () => { try { await recordSendMut.mutateAsync({ certificationId, contractId, revisionId: revision.id, sentTo, sendNote }); await invalidate(); onMutated(); toast.success("Envío registrado; la revisión queda congelada."); } catch(e: any) { toast.error(getContractErrorMessage(e)); } }}>Registrar envío</Button></div><Textarea aria-label="Nota de envío" value={sendNote} onChange={e => setSendNote(e.target.value)} placeholder="Nota o contexto del envío (opcional)" rows={2}/></div>
                  )}
                  {revision.status === "sent" && <>
                    <div className="flex flex-wrap gap-2">{(revision.documents ?? []).map((doc: any) => <a key={doc.id} className="text-sm underline" href={doc.downloadUrl} target="_blank" rel="noreferrer">{doc.documentType === "signed_received" ? "Firmado recibido" : doc.filename ?? "Documento"}</a>)}</div>
                    <CertificationDocumentsPanel certificationId={certificationId} contractId={contractId} canManageDocs={caps.canManageDocs ?? false} revisionId={revision.id} onDocumentMutated={newTs => { if(newTs) setActiveUpdatedAt(newTs); void invalidate(); onMutated(); }} />
                  </>}
                </div>
              ))}
              {status === "sent_to_client" && caps.canEditDraft && revisionsQuery.data?.some((r: any) => r.status === "sent") && (
                <div className="space-y-2"><Label htmlFor="cert-revision-reason">Motivo para nueva revisión</Label><Textarea id="cert-revision-reason" value={revisionReason} onChange={e => setRevisionReason(e.target.value)} placeholder="Describe el cambio solicitado"/><Button size="sm" variant="outline" disabled={!revisionReason.trim() || revisionSaveMut.isPending} onClick={async () => { try { const active = revisionsQuery.data?.find((r: any) => r.status === "sent"); if(!active) return; await revisionSaveMut.mutateAsync({ certificationId, contractId, reason: revisionReason, payload: active.payload }); setRevisionReason(""); setGeneratedPdfUrl(null); await invalidate(); onMutated(); toast.success("Nueva revisión creada; la anterior permanece inmutable."); } catch(e: any) { toast.error(getContractErrorMessage(e)); } }}>Crear siguiente revisión</Button></div>
              )}
            </section>

            <Separator />

            {/* Timeline */}
            {(timelineQuery.isLoading || (timelineQuery.data?.length ?? 0) > 0) && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Historial</p>
                {timelineQuery.isLoading ? (
                  <Skeleton className="h-16 w-full" />
                ) : (
                  <div className="space-y-1 text-xs text-muted-foreground border-l-2 border-border pl-3">
                    {timelineQuery.data?.map((event) => (
                      <div key={`${event.event}-${event.at}`}>
                        <span className={event.event === "cancelled" || event.event === "rejected" ? "text-destructive" : "text-foreground"}>
                          {event.label}
                        </span>{" "}— {new Date(event.at).toLocaleString("es-ES")}
                        {event.actorUserId != null ? ` — Usuario #${event.actorUserId}` : ""}
                        {event.note ? ` — ${event.note}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Documents */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Documentos</p>
              <CertificationDocumentsPanel
                certificationId={certificationId}
                contractId={contractId}
                canManageDocs={caps.canManageDocs ?? false}
                revisionId={revisionsQuery.data?.find((r: any) => r.status === "preparing" || r.status === "accepted" || r.status === "sent")?.id ?? null}
                onDocumentMutated={(newTs) => {
                  if (newTs) setActiveUpdatedAt(newTs);
                  invalidate();
                  onMutated();
                }}
              />
            </div>

            {showDeclarePm && status === "draft" && caps.canPmReview && (
              <div className="space-y-2 rounded border p-3" role="group" aria-labelledby="declare-pm-title">
                <p id="declare-pm-title" className="font-medium">¿Ya revisada por PM?</p>
                <p>Confirmas que esta certificación ya ha sido revisada por PM. Se omitirá la solicitud de revisión PM. Quedará aprobada por PM, con tu usuario y la fecha de esta declaración en el historial. No se enviará al cliente ni se creará un hito.</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" autoFocus onClick={declarePmReviewed} disabled={declarePmMut.isPending}>
                    {declarePmMut.isPending ? "Registrando…" : "Confirmar revisión previa"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowDeclarePm(false)} disabled={declarePmMut.isPending}>Volver</Button>
                </div>
              </div>
            )}

            {showReject && (
              <div className="space-y-2">
                <Label>Motivo de rechazo (obligatorio)</Label>
                <Textarea
                  rows={2}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive"
                    disabled={!rejectionReason.trim() || transitionMut.isPending}
                    onClick={() => doTransition("rejected", { rejectionReason })}>
                    Confirmar rechazo
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowReject(false)}>Cancelar</Button>
                </div>
              </div>
            )}

            {showCancel && (
              <div className="space-y-2">
                <Label>Motivo de cancelación (obligatorio)</Label>
                <Textarea
                  rows={2}
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive"
                    disabled={!cancellationReason.trim() || transitionMut.isPending}
                    onClick={() => doTransition("cancelled", { cancellationReason })}>
                    Confirmar cancelación
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowCancel(false)}>Cancelar</Button>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {cert && !showReject && !showCancel && !showDeclarePm && (
          <DialogFooter className="flex-row flex-wrap shrink-0 min-w-0 items-center justify-end gap-2 [&>p]:w-full [&>p]:min-w-0 [&>p]:break-words [&>p]:whitespace-normal [&>button]:min-w-0 [&>button]:max-w-full [&>button]:whitespace-normal">
            {status === "draft" && caps.canEditDraft && !showEdit && (
              <Button size="sm" variant="outline" onClick={openEdit}>
                <Pencil className="w-3 h-3 mr-1" />
                Editar
              </Button>
            )}
            {status === "draft" && caps.canPmReview && !showEdit && (
              <Button size="sm" variant="outline" onClick={() => setShowDeclarePm(true)} disabled={transitionMut.isPending || updateMut.isPending || declarePmMut.isPending}>
                Ya revisada por PM
              </Button>
            )}
            {status === "draft" && caps.canSubmit && (
              <Button size="sm" onClick={() => doTransition("pending_pm_review")} disabled={transitionMut.isPending}>
                Enviar a revisión PM
              </Button>
            )}
            {status === "pending_pm_review" && caps.canPmReview && (
              <>
                <Button size="sm" onClick={() => doTransition("pm_approved")} disabled={transitionMut.isPending}>
                  Aprobar (PM)
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setShowReject(true)}>
                  Rechazar
                </Button>
              </>
            )}
            {status === "pm_approved" && caps.canSendToClient && (
              <>
                {legacyWithoutV1 ? (
                  <>
                    <p className="w-full min-w-0 break-words whitespace-normal text-xs text-muted-foreground">Registro histórico sin carátula V1. Confirma el envío solo después de haberlo realizado por el canal correspondiente; no se genera ni registra evidencia documental.</p>
                    <Button size="sm" onClick={() => setLegacyTransitionTarget("sent_to_client")} disabled={transitionMut.isPending}>Registrar envío histórico</Button>
                  </>
                ) : revisionsUnavailable ? (
                  <p className="text-xs text-muted-foreground" role={revisionsQuery.isError ? "alert" : "status"}>No se habilita el envío histórico hasta verificar las revisiones.</p>
                ) : null}
                <Button size="sm" variant="destructive" onClick={() => setShowCancel(true)}>Cancelar certificación</Button>
              </>
            )}
            {status === "sent_to_client" && (
              <>
                {caps.canClientAccept && legacyWithoutV1 && (
                  <>
                    <p className="text-xs text-muted-foreground">Flujo histórico sin carátula ni snapshot V1. La aceptación no añadirá ni modificará documentos históricos.</p>
                    <Button size="sm" onClick={() => setLegacyTransitionTarget("client_accepted")} disabled={transitionMut.isPending}>Registrar aceptación histórica</Button>
                  </>
                )}
                {caps.canClientAccept && hasRevisionHistory && (
                  <Button size="sm" onClick={() => setShowSignedRevisionConfirm(true)} disabled={transitionMut.isPending || revisionRows.some((r: any) => r.status === "preparing") || !revisionRows.some((r: any) => r.status === "sent" && r.signedDocumentId)}>
                    Marcar como aceptado
                  </Button>
                )}
                {caps.canClientAccept && revisionsUnavailable && <p className="text-xs text-muted-foreground" role={revisionsQuery.isError ? "alert" : "status"}>No se habilita la aceptación histórica hasta verificar las revisiones.</p>}
                {caps.canClientAccept && hasRevisionHistory && revisionRows.some((r: any) => r.status === "preparing") && <p className="text-xs text-muted-foreground">No se puede aceptar mientras haya una revisión en preparación. Completa y envía la nueva revisión primero.</p>}
                {caps.canPmReview && (
                  <Button size="sm" variant="destructive" onClick={() => setShowReject(true)}>
                    Rechazar
                  </Button>
                )}
                {caps.canCancel && (
                  <Button size="sm" variant="outline" onClick={() => setShowCancel(true)}>
                    Cancelar
                  </Button>
                )}
              </>
            )}
            {status === "rejected" && caps.canEditDraft && (
              <Button size="sm" variant="outline" onClick={() => doTransition("draft")} disabled={transitionMut.isPending}>
                Reabrir como borrador
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>
              Cerrar
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
    <Dialog open={legacyTransitionTarget != null} onOpenChange={open => { if (!open) setLegacyTransitionTarget(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{legacyTransitionTarget === "sent_to_client" ? "Confirmar envío histórico" : "Confirmar aceptación histórica"}</DialogTitle>
          <DialogDescription>
            {legacyTransitionTarget === "sent_to_client"
              ? "Confirma únicamente que la certificación ya se envió por el canal correspondiente. Se conservará el flujo histórico, sin crear carátula V1 ni registrar evidencia documental."
              : "Confirma la aceptación dentro del flujo histórico. No se crea una revisión V1 ni se atribuye una verificación documental que este registro no contiene."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-wrap">
          <Button variant="outline" onClick={() => setLegacyTransitionTarget(null)}>Volver</Button>
          <Button onClick={async () => { const target = legacyTransitionTarget; setLegacyTransitionTarget(null); if (target) await doTransition(target); }} disabled={!legacyWithoutV1 || transitionMut.isPending}>
            {legacyTransitionTarget === "sent_to_client" ? "Confirmar envío histórico" : "Confirmar aceptación histórica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={showSignedRevisionConfirm} onOpenChange={setShowSignedRevisionConfirm}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Confirmar correspondencia del firmado</DialogTitle><DialogDescription>Confirma explícitamente que el documento firmado recibido corresponde a la revisión enviada que se muestra en esta ficha. La aplicación no autentica por sí sola el contenido del PDF.</DialogDescription></DialogHeader>
        {(() => { const revision = revisionsQuery.data?.find((r: any) => r.status === "sent" && r.signedDocumentId); return revision ? <p className="text-sm">Revisión {revision.revisionNumber} · documento firmado #{revision.signedDocumentId}</p> : <p className="text-sm">No hay una revisión enviada con documento firmado.</p>; })()}
        <DialogFooter><Button variant="outline" onClick={() => setShowSignedRevisionConfirm(false)}>Cancelar</Button><Button onClick={async () => { setShowSignedRevisionConfirm(false); await doTransition("client_accepted"); }} disabled={!revisionsQuery.data?.some((r: any) => r.status === "sent" && r.signedDocumentId)}>Confirmar y aceptar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={showRevisionDiscardConfirm} onOpenChange={setShowRevisionDiscardConfirm}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>¿Descartar cambios?</DialogTitle><DialogDescription>Hay cambios sin guardar en esta revisión. Si cierras ahora, se perderán.</DialogDescription></DialogHeader>
        <DialogFooter><Button variant="outline" onClick={() => setShowRevisionDiscardConfirm(false)}>Seguir editando</Button><Button variant="destructive" onClick={() => { setShowRevisionDiscardConfirm(false); setEditingRevisionId(null); }}>Descartar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
